/**
 * # dxfBridge.conformantSkeleton.textStyles (T9b review round 1 — referenced STYLE records)
 *
 * On the FROM-SCRATCH conformant path every STYLE record must be minted a
 * handle and wired to the STYLE table head by the record machinery. The export
 * used to discover a text style referenced ONLY by name — an MLEADER's
 * `textStyleName`, a referenced MLEADERSTYLE's `textStyleName` (its `342`) —
 * AFTER the skeleton pass and appended a bare `{ name }` record with a
 * synthetic handle: no `330` owner, none of the `70 40 41 50 71 42 3 4` groups
 * AutoCAD / ezdxf always write (ezdxf completed it silently on load). The root
 * fix materialises such styles in the TYPED model BEFORE the skeleton's record
 * remint, so they get a collision-checked handle, the table-head owner and
 * the full canonical payload exactly like a user-created style.
 *
 * Source of truth: Autodesk DXF reference (STYLE / MLEADER 340 / MLEADERSTYLE
 * 342 are handle references to a STYLE record) + ezdxf 1.4.3.
 */
import { visitEmittedEntityLists } from "./dxfBridge.conformantSkeleton.emittedEntities";
import { textStyleSchema, type DrawingDocument } from "./model";

/** Structural view of the entities this pass inspects. */
interface MultileaderLike {
  type?: string;
  textStyleName?: string;
  styleName?: string;
}

/**
 * Lowercased text-style names the export will reference by name: every
 * emitted MLEADER's `textStyleName` and the `textStyleName` of every
 * MLEADERSTYLE an emitted MLEADER references. Walks the emitted entity lists
 * ({@link visitEmittedEntityLists}).
 */
function collectReferencedTextStyleNames(document: DrawingDocument): Map<string, string> {
  const referenced = new Map<string, string>(); // lower → as-authored
  const leaderStyleNames = new Set<string>();
  const note = (name: string | undefined): void => {
    const trimmed = name?.trim() ?? "";
    if (trimmed.length === 0) return;
    if (!referenced.has(trimmed.toLowerCase())) referenced.set(trimmed.toLowerCase(), trimmed);
  };
  const visit = (entities: readonly unknown[]): void => {
    for (const entity of entities) {
      const ml = entity as MultileaderLike;
      if (ml.type !== "Multileader") continue;
      note(ml.textStyleName);
      leaderStyleNames.add((ml.styleName?.trim() || "Standard").toLowerCase());
    }
  };
  visitEmittedEntityLists(document, visit);
  for (const style of document.styleTables?.multileaderStyles ?? []) {
    if (leaderStyleNames.has(style.name.toLowerCase())) note(style.textStyleName);
  }
  return referenced;
}

/**
 * Return `document.styleTables` with a typed default STYLE
 * (`textStyleSchema.parse({ name })`) appended for every referenced text-style
 * name that is absent (case-insensitive — DXF names are). Pure: the input is
 * never mutated; identity when nothing is missing. Handles / owners are left
 * for the record remint + owner wiring that follows.
 */
export function materializeReferencedTextStyles(
  document: DrawingDocument,
): DrawingDocument["styleTables"] {
  const styleTables = document.styleTables;
  const present = new Set((styleTables.textStyles ?? []).map((s) => s.name.toLowerCase()));
  const missing: string[] = [];
  for (const [lower, name] of collectReferencedTextStyleNames(document)) {
    if (!present.has(lower)) {
      present.add(lower);
      missing.push(name);
    }
  }
  if (missing.length === 0) return styleTables;
  return {
    ...styleTables,
    textStyles: [
      ...(styleTables.textStyles ?? []),
      ...missing.map((name) => textStyleSchema.parse({ name })),
    ],
  };
}
